import type { LineOverrideLike } from './bookStyleCss';
import { DEFAULT_BASE_FONT_SIZE_PX } from './bookDefaults';
import { DEFAULT_FONT_FAMILY } from './appFonts';
import {
  ALL_BORDER_SIDES,
  borderSidesToFormValue,
  formBorderSidesToStored,
  type BorderSide,
} from './borderSides';
import {
  getBaseFontFamilyToken,
  isBoldFromFontFamily,
  isDedicatedFontFamilyBase,
  isDedicatedWeightFontFamily,
  isItalicFromFontFamily,
  resolveStoredTypographyFromForm,
} from './fontFamily';

export type LineStyleFormValues = {
  fontSize?: number;
  color?: string;
  backgroundColor?: string;
  fontFamily?: string;
  fontWeight?: number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  align?: 'left' | 'center' | 'right' | 'justify' | 'justify-center';
  span?: 12 | 24;
  borderWidth?: number;
  borderStyle?: string;
  borderColor?: string;
  borderSides?: BorderSide[];
  borderRadius?: number;
  padding?: number;
  paddingLeft?: number;
  paddingRight?: number;
  paddingTop?: number;
  paddingBottom?: number;
  margin?: number;
  marginLeft?: number;
  marginRight?: number;
  marginTop?: number;
  marginBottom?: number;
  lineHeight?: number;
  textIndent?: number;
  widthPercent?: number;
  display?: '' | 'block' | 'inline' | 'inline-block';
};

export const LINE_STYLE_FORM_DEFAULTS: LineStyleFormValues = {
  fontSize: DEFAULT_BASE_FONT_SIZE_PX,
  color: '',
  backgroundColor: '',
  fontFamily: DEFAULT_FONT_FAMILY,
  bold: false,
  italic: false,
  underline: false,
  align: 'left',
  span: 24,
  borderWidth: 0,
  borderStyle: 'none',
  borderColor: '',
  borderSides: [...ALL_BORDER_SIDES],
  borderRadius: 0,
  padding: 0,
  paddingLeft: 0,
  paddingRight: 0,
  paddingTop: 0,
  paddingBottom: 0,
  margin: 0,
  marginLeft: 0,
  marginRight: 0,
  marginTop: 0,
  marginBottom: 0,
  lineHeight: 1,
  textIndent: 0,
  widthPercent: 0,
  display: '',
};

function positiveNum(v: unknown): number | undefined {
  if (v == null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function signedNum(v: unknown): number | undefined {
  if (v == null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) && n !== 0 ? n : undefined;
}

function lineHeightNum(v: unknown): number | undefined {
  if (v == null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

export function parseWidthPercent(width?: string | number): number {
  if (typeof width === 'string' && width.trim().endsWith('%')) {
    const n = parseFloat(width);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

export function widthPercentToCss(widthPercent?: number): string | undefined {
  const n = Number(widthPercent ?? 0);
  if (!Number.isFinite(n) || n <= 0 || n > 100) return undefined;
  return `${n}%`;
}

function isEmptyFormString(v: unknown): boolean {
  return v == null || (typeof v === 'string' && !v.trim());
}

const CLEARABLE_OVERRIDE_STRING_KEYS = ['color', 'backgroundColor', 'borderColor', 'fontFamily'] as const;

type ClearableOverrideStringKey = (typeof CLEARABLE_OVERRIDE_STRING_KEYS)[number];

function formOverrideStringForLoad(
  override: LineOverrideLike,
  key: ClearableOverrideStringKey,
  baseValue: string | undefined,
): string {
  if (Object.prototype.hasOwnProperty.call(override, key)) {
    const value = override[key];
    return typeof value === 'string' ? value : '';
  }
  return baseValue ?? '';
}

/** Resolved style for render — explicit empty override blocks template fallbacks. */
export function resolveOverrideStyleField(
  override: LineOverrideLike | undefined,
  key: ClearableOverrideStringKey,
  ...fallbacks: Array<string | undefined>
): string | undefined {
  if (override && Object.prototype.hasOwnProperty.call(override, key)) {
    const value = override[key];
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
  }
  for (const fallback of fallbacks) {
    if (typeof fallback === 'string' && fallback.trim()) return fallback.trim();
  }
  return undefined;
}

export function lineOverrideToFormValues(
  override: LineOverrideLike | undefined,
  defaults?: Partial<LineStyleFormValues>,
): LineStyleFormValues {
  const base = { ...LINE_STYLE_FORM_DEFAULTS, ...defaults };
  if (!override) return base;

  const loadedFontFamily = formOverrideStringForLoad(override, 'fontFamily', base.fontFamily);
  const normalizedFamily = loadedFontFamily ? getBaseFontFamilyToken(loadedFontFamily) ?? loadedFontFamily : '';

  return {
    ...base,
    fontSize: override.fontSize ?? base.fontSize,
    color: formOverrideStringForLoad(override, 'color', base.color),
    backgroundColor: formOverrideStringForLoad(override, 'backgroundColor', base.backgroundColor),
    fontFamily: normalizedFamily,
    bold:
      isBoldFromFontFamily(override.fontFamily) ??
      ((override.fontWeight ?? 400) >= 600),
    italic:
      isItalicFromFontFamily(override.fontFamily) ??
      (override.fontStyle === 'italic'),
    underline: (override.textDecoration ?? '').includes('underline'),
    align: override.textAlign ?? base.align,
    borderWidth: override.borderWidth ?? base.borderWidth,
    borderStyle: override.borderStyle ?? base.borderStyle,
    borderColor: formOverrideStringForLoad(override, 'borderColor', base.borderColor),
    borderSides: borderSidesToFormValue(override as { borderSide?: unknown; borderSides?: unknown }),
    borderRadius: override.borderRadius ?? base.borderRadius,
    padding: override.padding ?? base.padding,
    paddingLeft: override.paddingLeft ?? base.paddingLeft,
    paddingRight: override.paddingRight ?? base.paddingRight,
    paddingTop: override.paddingTop ?? base.paddingTop,
    paddingBottom: override.paddingBottom ?? base.paddingBottom,
    margin: override.margin ?? base.margin,
    marginLeft: override.marginLeft ?? base.marginLeft,
    marginRight: override.marginRight ?? base.marginRight,
    marginTop: override.marginTop ?? base.marginTop,
    marginBottom: override.marginBottom ?? base.marginBottom,
    lineHeight: override.lineHeight ?? base.lineHeight,
    textIndent: override.textIndent ?? base.textIndent,
    widthPercent: parseWidthPercent(override.width) || base.widthPercent,
    display: override.display ?? base.display,
    ...(override.fontWeight != null ? { fontWeight: override.fontWeight } : {}),
    span:
      (override as { span?: unknown }).span === 12 || (override as { span?: unknown }).span === 24
        ? ((override as { span: 12 | 24 }).span as 12 | 24)
        : base.span,
  };
}

function resolveFontWeightFromForm(values: Record<string, unknown>): number | undefined {
  if (isDedicatedWeightFontFamily(values.fontFamily)) {
    return undefined;
  }

  const hasBold = 'bold' in values;
  const boldOn = Boolean(values.bold);
  const hasWeight = values.fontWeight != null && values.fontWeight !== '';
  const weight = hasWeight ? Number(values.fontWeight) : NaN;
  const weightIsFinite = Number.isFinite(weight);

  // Header weight picker (400 / 600 / 700) — always trust explicit weight.
  if (weightIsFinite) {
    if (weight === 400 && hasBold && boldOn) return 700;
    if (weight === 400 || weight === 600 || weight === 700) return weight;
  }

  if (hasBold && weightIsFinite) {
    const implied = boldOn ? 700 : 400;
    // Line bold toggle on but weight still normal from stale hydration.
    if (boldOn && weight === 400) return 700;
    if (weight === implied) return weight;
    return implied;
  }
  if (weightIsFinite) return weight;
  if (hasBold) return boldOn ? 700 : 400;
  return undefined;
}

function applyBorderSidesFromForm(out: LineOverrideLike, values: Record<string, unknown>): void {
  const stored = formBorderSidesToStored(values);
  delete (out as { borderSide?: unknown }).borderSide;
  delete (out as { borderSides?: unknown }).borderSides;
  Object.assign(out, stored);
}

export function formValuesToLineOverride(
  values: Record<string, unknown>,
  opts?: { content?: string },
): LineOverrideLike {
  const width = widthPercentToCss(values.widthPercent as number | undefined);
  const displayRaw = values.display;
  const display =
    displayRaw === 'block' || displayRaw === 'inline' || displayRaw === 'inline-block'
      ? displayRaw
      : undefined;
  const typography = resolveStoredTypographyFromForm(values);
  const out: LineOverrideLike = {
    ...(opts?.content != null ? { content: opts.content } : {}),
    fontSize: values.fontSize != null ? Number(values.fontSize) : undefined,
    color: typeof values.color === 'string' && values.color.trim() ? values.color.trim() : undefined,
    backgroundColor:
      typeof values.backgroundColor === 'string' && values.backgroundColor.trim()
        ? values.backgroundColor.trim()
        : undefined,
    fontFamily: typography.fontFamily,
    fontWeight: typography.fontWeight,
    fontStyle: typography.fontStyle,
    textDecoration: values.underline ? 'underline' : undefined,
    textAlign: values.align as LineOverrideLike['textAlign'],
    borderWidth: positiveNum(values.borderWidth),
    borderStyle:
      values.borderStyle && values.borderStyle !== 'none'
        ? (values.borderStyle as LineOverrideLike['borderStyle'])
        : undefined,
    borderColor: typeof values.borderColor === 'string' && values.borderColor.trim() ? values.borderColor.trim() : undefined,
    borderRadius: positiveNum(values.borderRadius),
    padding: positiveNum(values.padding),
    paddingLeft: positiveNum(values.paddingLeft),
    paddingRight: positiveNum(values.paddingRight),
    paddingTop: positiveNum(values.paddingTop),
    paddingBottom: positiveNum(values.paddingBottom),
    margin: positiveNum(values.margin),
    marginLeft: positiveNum(values.marginLeft),
    marginRight: positiveNum(values.marginRight),
    marginTop: positiveNum(values.marginTop),
    marginBottom: positiveNum(values.marginBottom),
    lineHeight: lineHeightNum(values.lineHeight),
    textIndent: signedNum(values.textIndent),
    width,
    display,
  };
  applyBorderSidesFromForm(out, values);

  return out;
}

/** Convert tag style form draft to stored override (delta only; merges with existing on flush). */
export function tagStyleDraftToStoredOverride(
  draft: Record<string, unknown>,
): LineOverrideLike & { span?: 12 | 24 } {
  const out = formValuesToStyleOnlyOverride(draft);
  const next: LineOverrideLike = { ...out };
  if ('bold' in draft || 'italic' in draft || 'underline' in draft || draft.fontWeight != null) {
    applyTypographyTogglesToOverride(next, draft);
  }
  const span = draft.span === 12 || draft.span === 24 ? draft.span : undefined;
  return span ? { ...next, span } : next;
}

/** Style fields controlled by line-style forms — replaced wholesale on apply. */
export const FORM_STYLE_FIELD_KEYS = [
  'fontSize',
  'color',
  'backgroundColor',
  'fontFamily',
  'fontWeight',
  'fontStyle',
  'textDecoration',
  'textAlign',
  'borderWidth',
  'borderStyle',
  'borderColor',
  'borderSide',
  'borderSides',
  'borderRadius',
  'padding',
  'paddingLeft',
  'paddingRight',
  'paddingTop',
  'paddingBottom',
  'margin',
  'marginLeft',
  'marginRight',
  'marginTop',
  'marginBottom',
  'lineHeight',
  'textIndent',
  'width',
  'display',
] as const satisfies readonly (keyof LineOverrideLike)[];

type PreservedOverrideFields = {
  content?: string;
  objectFit?: string;
};

/** Style-only override for bulk apply — omits default bold/normal/line-height noise. */
export function formValuesToStyleOnlyOverride(values: Record<string, unknown>): LineOverrideLike {
  const out = formValuesToLineOverride(values);
  if (isDedicatedWeightFontFamily(values.fontFamily)) {
    delete out.fontWeight;
  }
  if (!values.bold && (values.fontWeight == null || values.fontWeight === '')) {
    delete out.fontWeight;
  }
  if (!values.italic) {
    delete out.fontStyle;
  }
  if (!values.underline) {
    delete out.textDecoration;
  }
  const lineHeight = Number(values.lineHeight ?? LINE_STYLE_FORM_DEFAULTS.lineHeight);
  if (!Number.isFinite(lineHeight) || lineHeight === LINE_STYLE_FORM_DEFAULTS.lineHeight) {
    delete out.lineHeight;
  }
  return out;
}

/** When the style form includes typography toggles, write explicit on/off values (so base styles do not bleed back). */
export function applyTypographyTogglesToOverride(
  out: LineOverrideLike,
  values: Record<string, unknown>,
  opts?: { clearWhenOff?: boolean },
): void {
  const clearWhenOff = opts?.clearWhenOff !== false;
  const isDedicated = isDedicatedFontFamilyBase(values.fontFamily);

  if (!isDedicated) {
    const resolvedFontWeight = resolveFontWeightFromForm(values);

    if (resolvedFontWeight != null) {
      out.fontWeight = resolvedFontWeight;
    } else if (clearWhenOff) {
      delete out.fontWeight;
    }

    if ('italic' in values) {
      if (values.italic) {
        out.fontStyle = 'italic';
      } else if (clearWhenOff) {
        out.fontStyle = 'normal';
      } else {
        delete out.fontStyle;
      }
    }
  }

  if ('underline' in values) {
    if (values.underline) {
      out.textDecoration = 'underline';
    } else if (clearWhenOff) {
      out.textDecoration = 'none';
    } else {
      delete out.textDecoration;
    }
  }
}

/** When optional string fields are cleared in the form, persist an explicit empty override. */
function applyOptionalStringClearsFromForm(out: LineOverrideLike, values: Record<string, unknown>): void {
  for (const key of CLEARABLE_OVERRIDE_STRING_KEYS) {
    if (key in values && isEmptyFormString(values[key])) {
      (out as Record<string, unknown>)[key] = '';
    }
  }
}

/** When the form resets border width/style to none, drop stored border fields from overrides. */
function applyBorderClearFromForm(out: LineOverrideLike, values: Record<string, unknown>): void {
  const width = values.borderWidth;
  const style = values.borderStyle;
  const noBorder =
    style === 'none' ||
    width === 0 ||
    width === '0' ||
    (width != null && width !== '' && Number.isFinite(Number(width)) && Number(width) === 0);
  if (!noBorder) return;
  delete out.borderWidth;
  delete out.borderStyle;
  delete out.borderColor;
  delete (out as { borderSide?: unknown }).borderSide;
  delete (out as { borderSides?: unknown }).borderSides;
}

/** Apply form styles to an override, clearing fields the form reset to empty/default. */
export function mergeFormStylesIntoOverride(
  prev: LineOverrideLike & PreservedOverrideFields,
  values: Record<string, unknown>,
): LineOverrideLike & PreservedOverrideFields {
  const fromForm = formValuesToLineOverride(values);
  const next: LineOverrideLike & PreservedOverrideFields = {};

  if (prev.content != null && prev.content !== '') {
    next.content = prev.content;
  }
  if (prev.objectFit != null && prev.objectFit !== '') {
    next.objectFit = prev.objectFit;
  }

  for (const key of FORM_STYLE_FIELD_KEYS) {
    const value = fromForm[key];
    if (value !== undefined) {
      Object.assign(next, { [key]: value });
    }
  }
  if ('borderSides' in values || 'borderSide' in values) {
    delete (next as { borderSide?: unknown }).borderSide;
    delete (next as { borderSides?: unknown }).borderSides;
    Object.assign(next, formBorderSidesToStored(values));
  }

  applyTypographyTogglesToOverride(next, values);
  applyOptionalStringClearsFromForm(next, values);
  applyBorderClearFromForm(next, values);

  return next;
}

/** Bulk apply (select / tag) — omits default bold/normal/line-height noise. */
export function mergeBulkFormStylesIntoOverride(
  prev: LineOverrideLike & PreservedOverrideFields,
  values: Record<string, unknown>,
): LineOverrideLike & PreservedOverrideFields {
  const fromForm = formValuesToStyleOnlyOverride(values);
  const fullForm = formValuesToLineOverride(values);
  const next: LineOverrideLike & PreservedOverrideFields = {};

  if (prev.content != null && prev.content !== '') {
    next.content = prev.content;
  }
  if (prev.objectFit != null && prev.objectFit !== '') {
    next.objectFit = prev.objectFit;
  }

  const preferStyleOnlyOnly = new Set<keyof LineOverrideLike>([
    'fontWeight',
    'fontStyle',
    'textDecoration',
    'lineHeight',
  ]);

  for (const key of FORM_STYLE_FIELD_KEYS) {
    const value =
      fromForm[key] ?? (preferStyleOnlyOnly.has(key) ? undefined : fullForm[key]);
    if (value !== undefined) {
      Object.assign(next, { [key]: value });
    }
  }
  if ('borderSides' in values || 'borderSide' in values) {
    delete (next as { borderSide?: unknown }).borderSide;
    delete (next as { borderSides?: unknown }).borderSides;
    Object.assign(next, formBorderSidesToStored(values));
  }

  applyTypographyTogglesToOverride(next, values);
  applyOptionalStringClearsFromForm(next, values);
  applyBorderClearFromForm(next, values);

  return next;
}
